import { AlertDialog, Button, XStack, YStack } from "tamagui";
import TextCustom, { TextSize } from "@components/TextCoustom";
import { colors } from "@styles/colors";

/*******************************************************************
 * Type: 提示框組件
 * Description:
 *     1. 用於顯示一個簡單的 POP out 提示框
 * Params:
 *     - isOpen: 控制提示框出現
 *     - onClose: 關閉提示框的回調函數
 *     - title: 提示框的標題，包括文本、顏色和大小（可選）
 *     - content: 提示框的內容，包括文本或 JSX 元素、顏色和大小（可選）
 *     - rightButton: 右側按鈕的屬性，包括文本、顏色、背景色、大小和點擊事件（可選）
 *     - leftButton: 左側按鈕的屬性，包括文本、顏色、背景色、大小和點擊事件（可選）
 ******************************************************************/

interface SimpleAlertProps {
  isOpen: boolean;
  onClose: () => void;
  title: {
    str: string;
    color?: string;
    size?: number;
  };
  content: {
    str: string | JSX.Element;
    color?: string;
    size?: number;
  };
  rightButton?: {
    str: string;
    color?: string;
    bg?: string;
    size?: number;
    onPress?: () => void;
  };
  centerButton?: {
    str: string;
    color?: string;
    bg?: string;
    size?: number;
    onPress?: () => void;
  };
  leftButton?: {
    str: string;
    color?: string;
    bg?: string;
    size?: number;
    onPress?: () => void;
  };
}

const SimpleAlert: React.FC<SimpleAlertProps> = ({ isOpen, onClose, title, content, rightButton, leftButton, centerButton }) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={onClose} native>
      <AlertDialog.Portal>
        <AlertDialog.Overlay key="overlay" animation="quick" opacity={0.6} enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} />
        <AlertDialog.Content
          bordered
          px={"$5"}
          py={"$5"}
          borderRadius={"$7"}
          w="90%"
          elevate
          key="content"
          animation={[
            "quick",
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          opacity={1}
          y={0}>
          <YStack gap="$5">
            <TextCustom str={title.str} type={TextSize.MEDIUM} align="left" color={title.color || "$color"} bold />

            {/* 動態應用樣式到 Content */}
            {typeof content.str === "string" ? (
              <TextCustom str={content.str} type={TextSize.NORMAL} align="left" color={content.color || "$color"} />
            ) : (
              content.str
            )}

            <XStack gap="$3" justifyContent="space-between" width="100%">
              <AlertDialog.Cancel asChild>
                <Button
                  flex={1}
                  textAlign="center"
                  fontSize={"$5"}
                  fontWeight="bold"
                  bg={rightButton?.bg ? rightButton.bg : colors.btnLightBlue}
                  color={rightButton?.color ? rightButton.color : "#6D6F72"}
                  onPress={rightButton?.onPress}>
                  {rightButton?.str || "取消"}
                </Button>
              </AlertDialog.Cancel>
              {centerButton && (
                <AlertDialog.Cancel asChild>
                  <Button
                    flex={1}
                    textAlign="center"
                    fontSize={"$5"}
                    fontWeight="bold"
                    bg={centerButton?.bg ? centerButton.bg : colors.btnLightBlue}
                    color={centerButton?.color ? centerButton.color : "#6D6F72"}
                    onPress={centerButton?.onPress}>
                    {centerButton?.str || "取消"}
                  </Button>
                </AlertDialog.Cancel>
              )}
              <AlertDialog.Action asChild>
                <Button
                  flex={1}
                  fontSize={"$5"}
                  textAlign="center"
                  fontWeight="bold"
                  theme="active"
                  bg={leftButton?.bg ? leftButton.bg : colors.btnBlue2}
                  color={leftButton?.color ? leftButton.color : "#FFF"}
                  onPress={leftButton?.onPress}>
                  {leftButton?.str || "確認"}
                </Button>
              </AlertDialog.Action>
            </XStack>
          </YStack>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog>
  );
};

export default SimpleAlert;
